<script setup lang="ts">
import { apps, formatting, useContext, useLocalization, useOrderViewContext } from '@hantera/portal-app';
import {
  ContextMenuSection,
  ContextMenuSections,
  InspectorLayout,
  InspectorProvider,
  InspectorTab,
  LabelTagDefinition,
  ToolbarItem
} from '@hantera/design-system';
import { computed, ComputedRef, reactive, ref, toRaw, watch, watchEffect } from 'vue';
import OrderHeader from './OrderHeader';
import OrderSidebar from './OrderSidebar';
import Deliveries from './Deliveries';
import Invoices from './Invoices';
import Payments from './Payments';
import Timeline from './Timeline';
import useBackend from '@/composition/useBackend';
import useApps from "@/composition/use-apps";
import log from "@/log";

const selectedTab = defineModel<string, string, string>('selectedTab', {
  default: 'deliveries',
  required: false
});

const viewportBreakpoint = 1024;

const orderViewContext = useOrderViewContext();

const t = useLocalization();
const backend = useBackend();
const appsContext = useApps()
const context = useContext()

let serviceTabIdx = 0
const serviceTabs = appsContext.getServices(apps.standardServices.orderView.tabProvider)?.reduce((res, provider) => {
  const tabs = provider.build(context, orderViewContext)
  if (!Array.isArray(tabs)) {
    return res
  }
  for (const tab of tabs) {
    res[`service${serviceTabIdx++}`] = tab
  }
  return res
}, {} as Record<string, { label: string | ComputedRef<string>, tags?: LabelTagDefinition[] | ComputedRef<LabelTagDefinition[]>, viewUri: string, toolbar?: ToolbarItem[] }>) ?? {}

/**
 * The download anchor element used to trigger the download of the debug dump
 */
const download = ref<HTMLAnchorElement>();

const canCancelOrder = computed(() => {
  const state = orderViewContext.state
  if (state.orderState === 'cancelled') return false
  if (state.invoices?.length) return false
  return (state.deliveries ?? []).every(
    d => d.deliveryState === 'open' || d.deliveryState === 'cancelled'
  )
})

const contextMenu = computed(() => {

  const orderActions: ContextMenuSection = []

  if (orderViewContext.state.orderState === 'pending') {
    orderActions.push({
      label: 'Confirm order',
      async action() {
        await orderViewContext.pushPendingChange(
          'setOrderState',
          [{
            type: 'setOrderState',
            orderState: 'confirmed',
          }],
          'Confirmed order'
        )
      }
    })
  }

  if (canCancelOrder.value) {
    orderActions.push({
      label: 'Cancel order',
      async action() {
        await orderViewContext.pushPendingChange(
          'setOrderState',
          [{
            type: 'setOrderState',
            orderState: 'cancelled',
          }],
          'Cancelled order'
        )
      }
    })
  }

  const result: ContextMenuSections = []

  if (orderActions.length > 0) {
    result.push(orderActions)
  }

  // The activity log answers what the platform decided; the trace answers what it said to the
  // outside world. Omitted rather than disabled without the permission: an entry that can never work
  // is noise.
  if (context.authorize('traces:read')) {
    result.push([
      {
        label: t('components.order.contextMenu.viewTraces'),
        action: () => {
          context.openView('/system/monitoring', {
            query: {
              tab: 'traces',
              resource: `actors/order/${orderViewContext.state.orderId}`
            }
          })
        }
      }
    ])
  }

  result.push([
    {
      label: 'Print order to console',
      hidden: true,
      action: () => {
        console.log(toRaw(orderViewContext.state));
      }
    }, {
      label: 'Download debug dump',
      hidden: true,
      action: async () => {
        if (!download.value) {
          console.error('Download ref not found');
          close();
          return;
        }

        const dump = await backend.orders.getDebugDump(orderViewContext.state.orderId);

        const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        download.value.href = url;
        download.value.download = `order-${orderViewContext.state.orderId}-debug-dump.json`;
        download.value.click();
      }
    }
  ] as ContextMenuSection)

  for (const provider of appsContext.getServices(apps.standardServices.orderView.contextMenuProvider)) {
    const section = provider.buildContextMenu?.(context, orderViewContext)
    if (section && section.length > 0) {
      result.push(section)
    }
  }

  return result
})

const layoutProvider: InspectorProvider = reactive(<InspectorProvider>{
  title: orderViewContext.state.orderNumber,
  summary: OrderSidebar,
  selectedTab: selectedTab.value,
  tabs: [],
})

const serviceInspectorTabs: InspectorTab[] = []

for (const tabId in serviceTabs) {
  const tab = serviceTabs[tabId]

  if (!tab.viewUri) {
    log.error(`orderView.tabProvider result was missing 'viewUri' property - Skipping`)
    continue;
  }

  const tabState = ref<Record<string, any>>()

  watch(() => context.currentView.value?.state?.tabs?.[tabId], (val) => {

    if (!val) {
      tabState.value = {}
      return
    }

    tabState.value = val
  }, { immediate: true, deep: true })

  watch(tabState, () => {
    if (!context.currentView.value?.state) {
      return
    }

    if (!context.currentView.value.state.tabs) {
      context.currentView.value.state.tabs = {}
    }

    context.currentView.value.state.tabs[tabId] = tabState.value
  }, { deep: true })

  const props = reactive({
    uri: tab.viewUri,
    state: tabState
  })

  serviceInspectorTabs.push({
    id: tabId,
    label: tab.label,
    tags: tab.tags,
    component: 'EmbeddedView',
    toolbar: tab.toolbar,
    props
  })
}

watchEffect(() => {

  layoutProvider.labels = [
    orderViewContext.state.channelKey ?? '',
    orderViewContext.state.currencyCode,
    {
      label: formatting.date(orderViewContext.state.createdAt),
      tooltip: formatting.dateTime(orderViewContext.state.createdAt)
    },
  ]

  layoutProvider.tabs = [{
    id: 'deliveries',
    label: t('components.order.tabs.deliveries'),
    tags: orderViewContext.state.deliveries?.length > 0 ? [{
      style: 'primary',
      value: formatting.number(orderViewContext.state.deliveries.length),
    }] : [],
    component: Deliveries,
  }, {
    id: 'invoices',
    label: t('components.order.tabs.invoices'),
    tags: orderViewContext.state.invoices?.length > 0 ? [{
      style: 'primary',
      value: formatting.number(orderViewContext.state.invoices.length),
    }] : [],
    component: Invoices,
  }, {
    id: 'payments',
    label: t('components.order.tabs.payments'),
    tags: orderViewContext.state.payments?.length > 0 ? [{
      style: 'primary',
      value: formatting.number(orderViewContext.state.payments.length),
    }] : [],
    component: Payments,
  }, {
    id: 'timeline',
    label: t('components.order.tabs.timeline'),
    component: Timeline,
  },
  ...serviceInspectorTabs
  ]

})



watchEffect(() => {
  let inspectorState: {
    label: string
    hue?: number
  } = {
    label: 'Pending'
  }

  if (orderViewContext.state.orderState === 'confirmed') {
    inspectorState = {
      label: 'Confirmed',
      hue: 133.42
    }
  } else if (orderViewContext.state.orderState === 'cancelled') {
    inspectorState = {
      label: 'Cancelled',
      hue: 18.87
    }
  }

  layoutProvider.state = inspectorState
})

watch(() => layoutProvider.selectedTab, () => {
  selectedTab.value = layoutProvider.selectedTab ?? 'deliveries'
})

</script>

<template>
  <a ref="download" style="display: none"></a>
  <InspectorLayout :provider="layoutProvider" v-context-menu="contextMenu">
    <template #header>
      <OrderHeader />
    </template>
  </InspectorLayout>
</template>
